import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { parseISO, format } from 'date-fns';

import {
  FaIdCard,
  FaPlus,
  FaArrowAltCircleUp,
  FaBirthdayCake,
} from 'react-icons/fa';
import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import {
  formataTelefone,
  formataCPF,
  formataCNPJ,
  formataIdade,
} from '../../utils/funcoes';

import { useToast } from '../../hooks/toast';
import { useClinica } from '../../hooks/clinica';

import Input from '../../components/Input';
import Loading from '../../components/Loading';
import Paginacao from '../../components/Paginacao';

import getValidationErrors from '../../utils/getValidationErrors';

import { PacienteLinha, PacienteLista } from './styles';
import getResponseErrors from '../../utils/getResponseErrors';

interface IPaciente {
  PacCodigo: number;
  PacPessoaFisica: boolean;
  PacNome: string;
  PacNomeSocial: string;
  PacDataNasc: Date;
  PacCPF: string;
  PacCNPJ: string;
  PacTelefone: string;
  PacCelular: string;
  PacEmail: string;
  avatar_url: string;
  idade: string;
  dataNascFormatada: string;
  telefoneFormatado: string;
  plano?: {
    PlaCodigo: number;
    status: string;
    color: string;
  };
}

interface IFormBusca {
  termoBusca: string;
}

interface IPaginacao {
  termoBusca: string;
  limite: number;
  pagina: number;
}

const Paciente: React.FC = () => {
  const { addToast } = useToast();
  const { imagensClinica } = useClinica();

  const semFoto =
    imagensClinica.find(img => img.nome === 'semFoto')?.arquivo_url || '';

  const history = useHistory();
  const formRefBusca = useRef<FormHandles>(null);
  const [pacientes, setPacientes] = useState<IPaciente[] | undefined>(
    undefined,
  );
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);
  const [paginacao, setPaginacao] = useState<IPaginacao>();

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        const response = await api.get(
          `/pacientes?termoBusca=${paginacao.termoBusca}&pagina=${paginacao.pagina}&limite=${paginacao.limite}`,
        );
        const pacientesApi: IPaciente[] = response.data[0];
        const total = response.data[1];

        const pacientesFormatados = pacientesApi.map(pac => {
          const dataNasc =
            pac.PacDataNasc && pac.PacDataNasc.toString().length > 9
              ? parseISO(pac.PacDataNasc.toString())
              : new Date();

          const telefoneFormatado =
            pac.PacTelefone && pac.PacTelefone.length > 0
              ? `${formataTelefone(pac.PacCelular)} - ${formataTelefone(
                  pac.PacTelefone,
                )}`
              : formataTelefone(pac.PacCelular);

          return {
            ...pac,
            dataNascFormatada: format(dataNasc, 'dd/MM/yyyy'),
            telefoneFormatado,
            PacCPF: formataCPF(pac.PacCPF),
            PacCNPJ: formataCNPJ(pac.PacCNPJ),
            idade: formataIdade(dataNasc, false),
            avatar_url: pac.avatar_url ? pac.avatar_url : semFoto,
            plano: pac.plano
              ? {
                  ...pac.plano,
                  color: pac.plano.status === 'Em Dia' ? 'primary' : 'danger',
                }
              : undefined,
          };
        });

        setPacientes(pacientesFormatados);

        setTotalRegistros(total);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível buscar os dados do paciente',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao, semFoto]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        termoBusca: Yup.string(),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setPacientes(undefined);

      setPaginacao({
        termoBusca: dados.termoBusca,
        pagina: 1,
        limite: 20,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    }
  }, []);

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="bg-white border p-2 mb-2">
          <div className="d-flex justify-content-between mb-2">
            <h2 id="topoBusca">Pacientes</h2>
            <div>
              <Button
                type="button"
                variant="success"
                size="sm"
                onClick={() => history.push('/pacientecadastro/0')}
              >
                <FaPlus size={18} /> Novo Paciente
              </Button>
            </div>
          </div>

          <Form ref={formRefBusca} onSubmit={handleSubmitBusca}>
            <Row>
              <Col sm={12} md={9}>
                <div className="d-flex mb-2">
                  <div style={{ flex: 1 }}>
                    <Input
                      name="termoBusca"
                      placeholder="NOME, TELEFONE OU CPF DO PACIENTE"
                      maxLength={100}
                      minLength={3}
                    />
                  </div>
                  <div>
                    <Button type="submit" size="sm" variant="outline-secondary">
                      BUSCAR
                    </Button>
                  </div>
                </div>
              </Col>
              <Col sm={12} md={3} className="text-right">
                <Button
                  type="button"
                  variant="outline-info"
                  size="sm"
                  onClick={() => history.push('/aniversariantes')}
                >
                  <FaBirthdayCake size={18} /> Aniversariantes
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
        <div className="bg-white border p-2">
          {pacientes ? (
            pacientes.map(paciente => (
              <PacienteLinha
                className="row"
                key={`paciente-${paciente.PacCodigo}`}
                id={`paciente-${paciente.PacCodigo}`}
              >
                <div className="col-md-7 col-lg-6">
                  <Link to={`pacientecadastro/${paciente.PacCodigo}`}>
                    <PacienteLista>
                      <img src={paciente.avatar_url} alt={paciente.PacNome} />
                      <div>
                        {paciente.PacNomeSocial?.trim() !== '' && (
                          <>
                            <span>
                              Nome Social: <b>{paciente.PacNomeSocial}</b>
                            </span>
                          </>
                        )}
                        <b>{paciente.PacNome}</b>
                        {paciente.PacPessoaFisica ? (
                          <>
                            <span>Nascimento:</span>{' '}
                            {paciente.dataNascFormatada} - {paciente.idade}{' '}
                            <span>CPF:</span> {paciente.PacCPF}
                          </>
                        ) : (
                          <>
                            <span>CNPJ:</span> {paciente.PacCNPJ}
                          </>
                        )}
                      </div>
                    </PacienteLista>
                  </Link>
                </div>
                <div className="col-md-3">
                  <PacienteLista>
                    <span>Telefone: </span> {paciente.telefoneFormatado}
                  </PacienteLista>
                </div>
                <div className="col-md-2  col-lg-3">
                  {paciente.plano && (
                    <div
                      className={`alert alert-${paciente.plano.color} mb-0 d-block`}
                      style={{ maxWidth: '200px' }}
                    >
                      <Link
                        to={`/cartaosaudedetalhe/${paciente.plano.PlaCodigo}`}
                        className={`d-flex justify-items-center text-${paciente.plano.color}`}
                      >
                        <FaIdCard size={13} className="mr-1" /> Cartão Saúde{' '}
                        {paciente.plano.status}
                      </Link>
                    </div>
                  )}
                </div>
              </PacienteLinha>
            ))
          ) : (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '250px' }}
            >
              <h5>Pesquise pacientes pelo Nome, pelo Telefone ou pelo CPF</h5>
            </div>
          )}

          {paginacao && (
            <Paginacao
              limitePagina={paginacao?.limite || 20}
              paginasVizinhas={2}
              totalRegistros={totalRegistros}
              paginaInicio={paginacao?.pagina}
              handleProximaPagina={handleProximaPagina}
            />
          )}

          {pacientes && (
            <a
              href="#topoBusca"
              className="btn btn-primary"
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '35px',
                opacity: 0.8,
              }}
            >
              <FaArrowAltCircleUp size={20} />
            </a>
          )}
        </div>
      </>
    </Container>
  );
};

export default Paciente;
